import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  Ban,
  Clock,
  Eye,
  History,
  KeyRound,
  Loader2,
  LogOut,
  RefreshCw,
  RotateCcw,
  Search,
  ShieldOff,
  UserX,
  X,
} from "lucide-react";
import { AdminPartners, tokenStorage } from "@/lib/api";

const ROLES = [
  { v: "all", label: "All roles" },
  { v: "advisor", label: "Advisor" },
  { v: "dsa", label: "DSA" },
  { v: "associate", label: "Associate Partner" },
  { v: "agency", label: "Agency Partner" },
  { v: "sub_branch", label: "Sub-Branch" },
  { v: "branch", label: "Branch" },
];

const STATUS_TAG = {
  active: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30",
  terminated: "bg-rose-500/15 text-rose-200 border-rose-500/40",
  disabled: "bg-white/10 text-white/60 border-white/20",
};

export default function AdminPartnersPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [role, setRole] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null); // { partner, mode: 'terminate' | 'reactivate' }
  const [resetting, setResetting] = useState(null); // { partner }
  const [audit, setAudit] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await AdminPartners.list({
        role,
        status,
        q,
        limit: 300,
      });
      setItems(r.items || []);
      setCounts(r.counts || {});
    } catch (e) {
      if (e?.response?.status === 401) {
        tokenStorage.clear();
        navigate("/admin", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }, [role, status, q, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const logout = () => {
    tokenStorage.clear();
    navigate("/admin", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#04102B] text-white">
      <header className="sticky top-0 z-30 bg-[#04102B]/95 backdrop-blur border-b border-[#D4AF37]/25">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/admin/leads"
              className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-[#F0D788]"
            >
              <ArrowLeft size={12} /> Back to Admin
            </Link>
            <div className="w-px h-6 bg-white/10 mx-2" />
            <h1 className="font-display text-base md:text-lg flex items-center gap-2">
              <ShieldOff size={16} className="text-[#F0D788]" /> Partners &amp; Termination
            </h1>
          </div>
          <button
            onClick={logout}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/15 hover:border-rose-400/40 text-xs text-white/80"
          >
            <LogOut size={12} /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-8">
        <section className="grid grid-cols-3 gap-3 mb-6">
          <Metric label="Active" value={counts.active || 0} accent="text-emerald-300" testId="p-active" />
          <Metric label="Terminated" value={counts.terminated || 0} accent="text-rose-300" testId="p-terminated" />
          <Metric label="Disabled (legacy)" value={counts.disabled || 0} accent="text-white/60" testId="p-disabled" />
        </section>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <select
            data-testid="p-role-filter"
            aria-label="Filter partners by role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="bg-white/[0.04] border border-white/10 rounded-full px-3 py-1.5 text-xs text-white/80 focus:outline-none focus:border-[#D4AF37]/60"
          >
            {ROLES.map((r) => (
              <option key={r.v} value={r.v} className="bg-[#04102B]">
                {r.label}
              </option>
            ))}
          </select>

          {[
            ["all", "All status"],
            ["active", "Active"],
            ["terminated", "Terminated"],
            ["disabled", "Disabled"],
          ].map(([v, label]) => (
            <button
              key={v}
              onClick={() => setStatus(v)}
              data-testid={`p-status-tab-${v}`}
              className={`px-3 py-1.5 rounded-full text-xs border transition ${
                status === v
                  ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#F0D788]"
                  : "border-white/10 text-white/60 hover:border-white/25"
              }`}
            >
              {label}
            </button>
          ))}

          <div className="relative ml-2">
            <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              data-testid="p-search"
              aria-label="Search partners by name, email, or phone"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, phone…"
              className="bg-white/[0.04] border border-white/10 rounded-full pl-8 pr-3 py-1.5 text-xs text-white/90 w-64 focus:outline-none focus:border-[#D4AF37]/60"
            />
          </div>

          <button
            onClick={load}
            disabled={loading}
            data-testid="p-refresh"
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-xs text-white/70"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        <section
          data-testid="p-table"
          className="bg-[#0A1F44] rounded-2xl border border-[#D4AF37]/20 overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-white/[0.04] text-[11px] uppercase tracking-wider text-white/55">
                <tr>
                  <th className="text-left px-4 py-3">Partner</th>
                  <th className="text-left px-4 py-3">Role</th>
                  <th className="text-left px-4 py-3">Contact</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Termination info</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-white/55">
                      <Loader2 className="inline animate-spin mr-2" size={13} /> Loading…
                    </td>
                  </tr>
                )}
                {!loading && items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-white/60">
                      No partners match this filter.
                    </td>
                  </tr>
                )}
                {!loading &&
                  items.map((p, i) => {
                    const st = p.status || "active";
                    const cls = STATUS_TAG[st] || STATUS_TAG.disabled;
                    return (
                      <tr
                        key={p.id}
                        data-testid={`p-row-${i}`}
                        className={`border-t border-white/5 ${
                          st === "terminated" ? "bg-rose-950/10" : ""
                        }`}
                      >
                        <td className="px-4 py-3">
                          <div className="text-white font-medium">{p.name}</div>
                          <div className="text-[11px] text-white/40 font-mono">
                            {p.id?.slice(0, 8)} · {p.referral_code || "—"}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-white/[0.06] border border-white/10 text-white/80">
                            {(p.role || "").replace("_", " ")}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-white/75">
                          <div>{p.email || "—"}</div>
                          <div className="text-[11px] text-white/45">{p.phone}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${cls}`}
                          >
                            {st === "terminated" && <Ban size={9} />}
                            {st}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-[11px]">
                          {st === "terminated" ? (
                            <>
                              <div className="text-rose-200 line-clamp-2 max-w-xs">
                                {p.termination_reason || "—"}
                              </div>
                              <div className="text-white/45 mt-1">
                                by {p.terminated_by || "admin"} ·{" "}
                                {p.terminated_at?.slice(0, 16).replace("T", " ")}
                              </div>
                            </>
                          ) : (
                            <span className="text-white/35">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <button
                            onClick={() => setAudit(p)}
                            data-testid={`p-audit-${i}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-white/15 hover:border-white/40 text-[11px] text-white/70 mr-1.5"
                          >
                            <History size={11} /> Audit
                          </button>
                          <button
                            onClick={() => setResetting({ partner: p })}
                            data-testid={`p-reset-password-${i}`}
                            title="Set a new password for this partner"
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-amber-400/40 hover:border-amber-400/80 text-[11px] text-amber-200 mr-1.5"
                          >
                            <KeyRound size={11} /> Reset PW
                          </button>
                          {st === "terminated" ? (
                            <button
                              onClick={() => setConfirm({ partner: p, mode: "reactivate" })}
                              data-testid={`p-reactivate-${i}`}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-emerald-400/40 hover:border-emerald-400/80 text-[11px] text-emerald-300"
                            >
                              <RotateCcw size={11} /> Reactivate
                            </button>
                          ) : (
                            <button
                              onClick={() => setConfirm({ partner: p, mode: "terminate" })}
                              data-testid={`p-terminate-${i}`}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-rose-400/40 hover:border-rose-400/80 text-[11px] text-rose-300"
                            >
                              <UserX size={11} /> Terminate
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      {confirm && (
        <ConfirmDialog
          partner={confirm.partner}
          mode={confirm.mode}
          busy={busy}
          onClose={() => setConfirm(null)}
          onConfirm={async (text) => {
            setBusy(true);
            try {
              if (confirm.mode === "terminate") {
                await AdminPartners.terminate(confirm.partner.id, text);
              } else {
                await AdminPartners.reactivate(confirm.partner.id, text);
              }
              setConfirm(null);
              await load();
            } catch (e) {
              alert(e?.response?.data?.detail || "Action failed");
            } finally {
              setBusy(false);
            }
          }}
        />
      )}

      {audit && <AuditDrawer partner={audit} onClose={() => setAudit(null)} />}
      {resetting && (
        <ResetPasswordDialog
          partner={resetting.partner}
          busy={busy}
          onClose={() => setResetting(null)}
          onConfirm={async (newPassword, reason) => {
            setBusy(true);
            try {
              await AdminPartners.resetPassword(resetting.partner.id, newPassword, reason);
              setResetting(null);
              await load();
            } catch (e) {
              alert(e?.response?.data?.detail || "Password reset failed");
            } finally {
              setBusy(false);
            }
          }}
        />
      )}
    </div>
  );
}

function Metric({ label, value, accent = "text-white", testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3.5"
    >
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/55">{label}</div>
      <div className={`mt-1.5 text-2xl font-display tabular-nums ${accent}`}>{value}</div>
    </div>
  );
}

function ConfirmDialog({ partner, mode, busy, onClose, onConfirm }) {
  const [text, setText] = useState("");
  const isTerm = mode === "terminate";
  const canSubmit = isTerm ? text.trim().length >= 6 : true;

  return (
    <div
      data-testid="p-confirm-dialog"
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`w-full max-w-md rounded-2xl border p-6 ${
          isTerm ? "border-rose-400/40 bg-[#1a0f13]" : "border-emerald-400/40 bg-[#0d1a12]"
        }`}
      >
        <div className="flex items-start gap-3 mb-4">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
              isTerm ? "bg-rose-500/15 text-rose-300" : "bg-emerald-500/15 text-emerald-300"
            }`}
          >
            {isTerm ? <AlertTriangle size={18} /> : <RotateCcw size={18} />}
          </div>
          <div className="flex-1">
            <div className="font-display text-lg text-white">
              {isTerm ? "Terminate partner?" : "Reactivate partner?"}
            </div>
            <div className="text-xs text-white/60 mt-1">
              {isTerm
                ? "This will immediately block the partner from logging in by email OR phone. This action is audit-logged."
                : "This will restore the partner's ability to log in."}
            </div>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="rounded-lg bg-white/[0.04] border border-white/10 p-3 mb-4 text-sm">
          <div className="text-white font-medium">{partner.name}</div>
          <div className="text-[11px] text-white/60">
            {partner.role?.toUpperCase()} · {partner.email || partner.phone}
          </div>
          <div className="text-[10px] text-white/40 font-mono mt-1">{partner.id?.slice(0, 8)}</div>
        </div>

        <label className="block text-[10px] uppercase tracking-[0.2em] text-white/55 mb-2">
          {isTerm ? "Reason for termination (required)" : "Note / justification (optional)"}
        </label>
        <textarea
          data-testid="p-reason-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          placeholder={
            isTerm
              ? "e.g. Repeated customer complaints for misrepresentation, verified by branch audit on…"
              : "e.g. Investigation completed — cleared of allegations"
          }
          className="w-full bg-white/[0.03] border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37]/60"
        />
        {isTerm && text.length > 0 && text.trim().length < 6 && (
          <div className="text-[11px] text-rose-300 mt-1">Reason must be at least 6 characters.</div>
        )}

        <div className="flex items-center justify-end gap-2 mt-5">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-full text-xs border border-white/15 text-white/70 hover:border-white/40"
          >
            Cancel
          </button>
          <button
            data-testid="p-confirm-submit"
            disabled={!canSubmit || busy}
            onClick={() => onConfirm(text.trim())}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition ${
              isTerm
                ? "bg-rose-500 hover:bg-rose-400 text-white disabled:opacity-40"
                : "bg-emerald-500 hover:bg-emerald-400 text-black disabled:opacity-40"
            }`}
          >
            {busy && <Loader2 size={12} className="animate-spin" />}
            {isTerm ? "Confirm Termination" : "Confirm Reactivation"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AuditDrawer({ partner, onClose }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await AdminPartners.audit(partner.id);
        setEvents(r.events || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [partner.id]);

  return (
    <div
      data-testid="p-audit-drawer"
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-stretch justify-end"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-[#0A1F44] border-l border-[#D4AF37]/30 overflow-y-auto p-6"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">Audit Trail</div>
            <div className="font-display text-lg mt-1 text-white">{partner.name}</div>
            <div className="text-[11px] text-white/55">
              {partner.role?.toUpperCase()} · {partner.email || partner.phone}
            </div>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white p-1" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {loading && (
          <div className="text-white/55 text-sm">
            <Loader2 className="inline animate-spin mr-2" size={13} /> Loading events…
          </div>
        )}

        {!loading && events.length === 0 && (
          <div className="text-white/55 text-sm">
            No termination or reactivation events recorded for this partner.
          </div>
        )}

        <div className="space-y-4">
          {events.map((e) => (
            <div
              key={e.id}
              className={`rounded-xl border p-4 ${
                e.action === "terminate"
                  ? "border-rose-400/30 bg-rose-500/[0.05]"
                  : "border-emerald-400/30 bg-emerald-500/[0.05]"
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                {e.action === "terminate" ? (
                  <Ban size={14} className="text-rose-300" />
                ) : (
                  <RotateCcw size={14} className="text-emerald-300" />
                )}
                <span
                  className={`text-[10px] uppercase tracking-[0.2em] font-semibold ${
                    e.action === "terminate" ? "text-rose-200" : "text-emerald-200"
                  }`}
                >
                  {e.action === "terminate" ? "Terminated" : "Reactivated"}
                </span>
                <span className="text-[10px] text-white/40 ml-auto flex items-center gap-1">
                  <Clock size={10} /> {e.at?.slice(0, 16).replace("T", " ")}
                </span>
              </div>
              {e.reason && (
                <div className="text-sm text-white/90 mb-2 leading-relaxed">
                  {e.reason}
                </div>
              )}
              <div className="text-[11px] text-white/50">by {e.by_admin_email || "—"}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ResetPasswordDialog({ partner, busy, onClose, onConfirm }) {
  const [pw, setPw] = useState("");
  const [reason, setReason] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [copied, setCopied] = useState(false);
  const isValid = pw.length >= 6;

  const generate = () => {
    // 12-char random password: uppercase, lowercase, digits + one symbol
    const alpha = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    const symbol = "@#$&*";
    let out = "";
    for (let i = 0; i < 11; i++) {
      out += alpha[Math.floor(Math.random() * alpha.length)];
    }
    out += symbol[Math.floor(Math.random() * symbol.length)];
    setPw(out);
    setShowPw(true);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(pw);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Clipboard API may be blocked in some browsers/contexts — surface silently in devtools.
      if (process.env.NODE_ENV === "development") console.debug("clipboard write blocked:", err);
    }
  };

  return (
    <div
      data-testid="p-reset-password-dialog"
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl border border-amber-400/40 bg-[#1a1508] p-6"
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-amber-500/15 text-amber-300">
            <KeyRound size={18} />
          </div>
          <div className="flex-1">
            <div className="font-display text-lg text-white">Reset Partner Password</div>
            <div className="text-xs text-white/60 mt-1 leading-relaxed">
              Set a new login password for this partner. The old password will stop
              working immediately. Share the new password with them via a trusted
              channel (phone call / WhatsApp).
            </div>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="rounded-lg bg-white/[0.04] border border-white/10 p-3 mb-4 text-sm">
          <div className="text-white font-medium">{partner.name}</div>
          <div className="text-[11px] text-white/60">
            {partner.role?.toUpperCase()} · {partner.email || partner.phone}
          </div>
        </div>

        <label className="block text-[10px] uppercase tracking-[0.2em] text-white/55 mb-2">
          New password (min 6 chars)
        </label>
        <div className="relative mb-2">
          <input
            data-testid="p-reset-pw-input"
            type={showPw ? "text" : "password"}
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="Enter or generate a new password"
            className="w-full bg-white/[0.03] border border-white/15 rounded-lg pl-3 pr-24 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-amber-400/60 font-mono"
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              className="text-[10px] text-white/60 hover:text-white px-1"
            >
              {showPw ? "Hide" : "Show"}
            </button>
            <button
              type="button"
              onClick={copy}
              disabled={!pw}
              className="text-[10px] text-amber-200 hover:text-amber-100 disabled:opacity-40 px-1"
            >
              {copied ? "Copied ✓" : "Copy"}
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={generate}
          data-testid="p-reset-pw-generate"
          className="text-[11px] text-amber-200 hover:text-amber-100 underline mb-4"
        >
          Generate strong password
        </button>

        <label className="block text-[10px] uppercase tracking-[0.2em] text-white/55 mb-2">
          Reason for reset (optional — for audit log)
        </label>
        <input
          data-testid="p-reset-reason-input"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Partner phoned — forgot credentials, no email access"
          className="w-full bg-white/[0.03] border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-amber-400/60"
        />

        <div className="mt-5 rounded-lg bg-amber-500/[0.08] border border-amber-500/25 px-3 py-2.5 text-[11px] text-amber-100 leading-relaxed">
          <strong className="text-amber-200">Note:</strong> The password is NEVER stored in
          plaintext — only its bcrypt hash. This reset action is audit-logged with your
          admin email and reason, but the password itself is not.
        </div>

        <div className="flex items-center justify-end gap-2 mt-5">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-full text-xs border border-white/15 text-white/70 hover:border-white/40"
          >
            Cancel
          </button>
          <button
            data-testid="p-reset-pw-submit"
            disabled={!isValid || busy}
            onClick={() => onConfirm(pw, reason.trim())}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold bg-amber-500 hover:bg-amber-400 text-black disabled:opacity-40 transition"
          >
            {busy && <Loader2 size={12} className="animate-spin" />}
            Set New Password
          </button>
        </div>
      </div>
    </div>
  );
}

